"use client";

import { sanitiseUrl, track } from "@/utilities/analytics";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * Emits `virtual_page_view` on every client-side route change.
 *
 * Never on the initial load, or on any full browser load: those are genuine
 * page views, which the container records itself.
 *
 * Deliberately keyed on pathname only. `/apply-online` re-pushes its query
 * string on every address and plan lookup, so firing on any history change
 * would count several page views for one page. Query params are read straight
 * off `window.location` rather than via `useSearchParams()`, which would both
 * re-fire this effect and force a Suspense boundary in the root layout.
 */
export const RouteTracker = () => {
	const pathname = usePathname();
	const previous = useRef<{ pathname: string; url: string } | null>(null);

	useEffect(() => {
		// Initial load. Record where the visitor landed so the first route
		// change has a referrer, and leave the page view to the container.
		if (!previous.current) {
			previous.current = { pathname, url: window.location.href };
			return;
		}

		// Strict Mode re-runs the mount effect without a route change.
		if (previous.current.pathname === pathname) {
			return;
		}

		const referrer = previous.current.url;

		// The document title is set by Next after the route commits, so read it
		// on the next tick or every soft navigation reports the previous page's
		// title.
		const timeout = window.setTimeout(() => {
			const currentUrl = window.location.href;

			track({
				event: "virtual_page_view",
				page_location: sanitiseUrl(currentUrl),
				page_title: document.title,
				page_referrer: sanitiseUrl(referrer),
			});

			previous.current = { pathname, url: currentUrl };
		}, 0);

		return () => window.clearTimeout(timeout);
	}, [pathname]);

	return null;
};
